import {
	forwardRef,
	useCallback,
	useImperativeHandle,
	useRef,
	useState
} from 'react';
import { TagChip } from '../../../../atoms/TagChip';

export type CategoriesSessionHandles = {
	onHighlightCategories: VoidFunction;
};

export type CategoriesSessionProps = {
	categories: Array<{
		category: string;
		isActive: boolean;
	}>;
};

export const CategoriesSession = forwardRef<
	CategoriesSessionHandles,
	CategoriesSessionProps
>(({ categories }, ref) => {
	const categoriesRef = useRef<HTMLDivElement>(null);

	const [isCategoryHighlighted, setIsCategoryHighlighted] = useState(false);

	const handleHighlightCategories = useCallback(() => {
		if (categories.length === 0) return null;

		categoriesRef.current?.scrollIntoView({
			behavior: 'smooth',
			block: 'end',
			inline: 'end'
		});

		setIsCategoryHighlighted(true);

		setTimeout(() => {
			setIsCategoryHighlighted(false);
		}, 1.5 * 1000);
	}, [categories.length]);

	useImperativeHandle(
		ref,
		() => ({
			onHighlightCategories: handleHighlightCategories
		}),
		[handleHighlightCategories]
	);

	if (categories.length === 0) return null;

	return (
		<div
			data-is-highlighted={isCategoryHighlighted}
			ref={categoriesRef}
			className="data-[is-highlighted=true]:animate-border-blinking flex w-full flex-col items-start gap-2 rounded-md border border-transparent p-1"
		>
			<strong className="flex items-center font-semibold text-dm-mid-grey">
				Categories
			</strong>

			<ul className="flex flex-wrap gap-2">
				{categories.map(({ category, isActive }) => (
					<li key={category} className="flex">
						<TagChip isActive={isActive}>{category}</TagChip>
					</li>
				))}
			</ul>
		</div>
	);
});
